import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import dayjs from 'dayjs'
import store from '@/app/ui/store'
import { Pagination } from '@/domain/entities/Pagination'
import {
  EventBus,
  EventBusConstants,
  LocalStorage,
  Utils,
} from '@/app/infrastructures/misc'
import {
  PublicVoucher,
  PublicVoucherData,
  PublicVoucherHistories,
  PublicVoucherHistory,
  PublicVouchers,
} from '@/domain/entities/PublicVoucher'
import { PublicVoucherPresenter } from '@/app/ui/presenters/PublicVoucherPresenter'
import {
  CreatePublicVoucherRequest,
  DeletePublicVoucherRequest,
  UpdatePublicVoucherRequest,
} from '@/data/payload/api/PublicVoucherRequest'
import {
  PUBLIC_VOUCHER_HISTORY_PAGINATION,
  PUBLIC_VOUCHER_PAGINATION,
} from '@/app/infrastructures/misc/Constants'

export interface PublicVoucherState {
  isLoading: boolean
  isDownloading: boolean
  publicVoucherData: PublicVoucher[]
  publicVoucherDetail: PublicVoucher
  paginationData: Pagination
  historyData: PublicVoucherHistory[]
  paginationHistory: Pagination
}

@Module({ namespaced: true, store, name: 'public-voucher', dynamic: true })
class PublicVoucherController extends VuexModule implements PublicVoucherState {
  private presenter: PublicVoucherPresenter = container.resolve(
    PublicVoucherPresenter
  )
  public isLoading = false
  public isDownloading = false
  public publicVoucherData = [new PublicVoucher()]
  public publicVoucherDetail = new PublicVoucher()
  public paginationData = new Pagination(1, PUBLIC_VOUCHER_PAGINATION, 0)
  public historyData = [new PublicVoucherHistory()]
  public paginationHistory = new Pagination(
    1,
    PUBLIC_VOUCHER_HISTORY_PAGINATION,
    0
  )
  public constants = {
    voucherPurposes: [
      {
        label: 'Redeem To Point',
        value: 'REDEEM_TO_POINT',
      },
    ],
    pointPurposes: [
      {
        label: 'Pickup Reguler',
        value: 'PICKUP',
      },
      {
        label: 'Drop paket Reguler',
        value: 'DROPOFF',
      },
      {
        label: 'Pickup Favorit',
        value: 'PICKUP_FAVORITE',
      },
      {
        label: 'Drop paket Favorit',
        value: 'DROPOFF_FAVORITE',
      },
      {
        label: 'BisaBelanja',
        value: 'SHOP',
      },
    ],
  }

  @Action({ rawError: true })
  public getPublicVoucherList(params: Record<string, any>) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        this.setPublicVoucherData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getPublicVoucherDetail(publicVoucherId: string) {
    this.setLoading(true)

    this.presenter
      .get(publicVoucherId)
      .then(res => {
        this.setPublicVoucherDetail(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public createPublicVoucher(form: {
    voucherName: string
    voucherCode: string
    voucherDailyLimit: number
    voucherPurpose: Record<string, any>
    voucherPointAmount: number
    voucherExpiry: number
    voucherPointPurpose: string[]
    voucherUsageLimit: number
    voucherPointExpiry: number
    startDate: string
    endDate: string
    startTime: string
    endTime: string
  }) {
    this.setLoading(true)
    const adminEmail = LocalStorage.getLocalStorage(
      LocalStorage.LOGIN_IDENTITY,
      true
    )

    this.presenter
      .create(
        new CreatePublicVoucherRequest(
          form.voucherName,
          form.voucherCode,
          form.voucherDailyLimit,
          form.voucherPurpose.value,
          form.voucherUsageLimit,
          new PublicVoucherData(
            form.voucherExpiry,
            form.voucherPointAmount,
            form.voucherPointPurpose.join(','),
            form.voucherPointExpiry
          ),
          dayjs(
            `${form.startDate} ${form.startTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format(),
          dayjs(
            `${form.endDate} ${form.endTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format(),
          adminEmail
        )
      )
      .then(() => {
        EventBus.$emit(EventBusConstants.CREATE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Create Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updatePublicVoucher(form: {
    publicVoucherId: string
    voucherName: string
    voucherCode: string
    voucherDailyLimit: number
    voucherPurpose: Record<string, any>
    voucherPointAmount: number
    voucherExpiry: number
    voucherPointPurpose: string[]
    voucherUsageLimit: number
    voucherPointExpiry: number
    startDate: string
    endDate: string
    startTime: string
    endTime: string
  }) {
    this.setLoading(true)
    const adminEmail = LocalStorage.getLocalStorage(
      LocalStorage.LOGIN_IDENTITY,
      true
    )

    this.presenter
      .update(
        form.publicVoucherId,
        new UpdatePublicVoucherRequest(
          form.voucherDailyLimit,
          form.voucherPurpose.value,
          form.voucherUsageLimit,
          new PublicVoucherData(
            form.voucherExpiry,
            form.voucherPointAmount,
            form.voucherPointPurpose.join(','),
            form.voucherPointExpiry
          ),
          dayjs(
            `${form.startDate} ${form.startTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format(),
          dayjs(
            `${form.endDate} ${form.endTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format(),
          adminEmail
        )
      )
      .then(() => {
        EventBus.$emit(EventBusConstants.UPDATE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Update Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public deletePublicVoucher(form: { publicVoucherId: string }) {
    this.setLoading(true)

    const payload = new DeletePublicVoucherRequest(
      LocalStorage.getLocalStorage(LocalStorage.LOGIN_IDENTITY, true)
    )

    this.presenter
      .delete(form.publicVoucherId, payload)
      .then(() => {
        EventBus.$emit(EventBusConstants.DELETE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Delete Public Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getVoucherHistory(form: { id: string; params: Record<string, any> }) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(form.params),
      'snake_case'
    )

    this.presenter
      .getHistories(form.id, formattedParams)
      .then(res => {
        this.setVoucherHistory(res)
      })
      .catch(error => {
        this.setVoucherHistory(
          new PublicVoucherHistories(this.paginationHistory, [
            new PublicVoucherHistory(),
          ])
        )
        Vue.notify({
          title: 'Fetch History Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getExportedFile(form: { id: string; params: Record<string, any> }) {
    this.setDownloading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(form.params),
      'snake_case'
    )

    this.presenter
      .exportHistories(form.id, formattedParams)
      .then(res => {
        EventBus.$emit(EventBusConstants.DOWNLOAD_VOUCHER_SUCCESS, {
          downloadUrl: res,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Download Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setDownloading(false)
      })
  }

  @Mutation
  private setPublicVoucherData(publicVoichers: PublicVouchers) {
    this.paginationData = publicVoichers.pagination as Pagination
    this.publicVoucherData = publicVoichers.data as PublicVoucher[]
  }

  @Mutation
  private setVoucherHistory(histories: PublicVoucherHistories) {
    this.paginationHistory = histories.pagination as Pagination
    this.historyData = histories.data as PublicVoucherHistory[]
  }

  @Mutation
  private setPublicVoucherDetail(publicVoucher: PublicVoucher) {
    this.publicVoucherDetail = publicVoucher
  }

  @Mutation
  private setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  private setDownloading(bool: boolean) {
    this.isDownloading = bool
  }
}

export default getModule(PublicVoucherController)
